import React, { useCallback, useRef } from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import s from "./Tabs.module.scss"

/**
 * Underlined tab strip. Controlled — the parent owns `activeKey`, which lets
 * the HES shell drive tabs from the URL without Tabs knowing about routing.
 *
 * Implements the WAI-ARIA tabs keyboard pattern (arrows / Home / End).
 *
 * @example
 * <Tabs
 *   items={[{ key: "history", label: "Command History", icon: <Clock size={15} /> }]}
 *   activeKey={tab}
 *   onChange={setTab}
 * />
 */
const Tabs = ({
  items = [],
  activeKey,
  onChange,
  className,
  ariaLabel = "Tabs",
  variant = "bar",
  sticky = false
}) => {
  const refs = useRef([])

  const focusTab = useCallback(
    (index) => {
      const enabled = items.filter((i) => !i.disabled)
      if (!enabled.length) return
      const bounded = (index + enabled.length) % enabled.length
      const target = enabled[bounded]
      const domIndex = items.findIndex((i) => i.key === target.key)
      refs.current[domIndex]?.focus()
      onChange?.(target.key)
    },
    [items, onChange]
  )

  const handleKeyDown = (event) => {
    const enabled = items.filter((i) => !i.disabled)
    const current = enabled.findIndex((i) => i.key === activeKey)

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault()
        focusTab(current + 1)
        break
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault()
        focusTab(current - 1)
        break
      case "Home":
        event.preventDefault()
        focusTab(0)
        break
      case "End":
        event.preventDefault()
        focusTab(enabled.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(s.tabs, { [s.inset]: variant === "inset", [s.sticky]: sticky }, className)}
      onKeyDown={handleKeyDown}
    >
      {items.map((item, index) => {
        const isActive = item.key === activeKey
        return (
          <button
            key={item.key}
            ref={(el) => {
              refs.current[index] = el
            }}
            role="tab"
            type="button"
            id={`tab-${item.key}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${item.key}`}
            // Only the active tab is in the tab order; arrows move between tabs.
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            className={cx(s.tab, { [s.active]: isActive })}
            onClick={() => !item.disabled && onChange?.(item.key)}
          >
            {item.icon && <span className={s.icon}>{item.icon}</span>}
            {item.label}
            {item.count != null && <span className={s.count}>{item.count}</span>}
          </button>
        )
      })}
    </div>
  )
}

const itemShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  icon: PropTypes.node,
  count: PropTypes.number,
  disabled: PropTypes.bool
})

Tabs.propTypes = {
  items: PropTypes.arrayOf(itemShape).isRequired,
  activeKey: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  /**
   * Pin the strip under the app header while the page scrolls.
   *
   * Needs an ancestor tall enough to stick within — a wrapper that ends just
   * below the strip releases it immediately. Where the strip sits under other
   * chrome (dashboard filters, the meter detail's back row) the whole group has
   * to pin together instead, so those screens stick their own wrapper.
   */
  sticky: PropTypes.bool,
  /**
   * "bar"   — full-bleed strip flush under the app header (module tabs)
   * "inset" — contained strip inside the content well (detail-page tabs)
   */
  variant: PropTypes.oneOf(["bar", "inset"])
}

/** Pairs with Tabs to wire up the aria relationship and hide inactive content. */
export const TabPanel = ({ tabKey, activeKey, children, className, keepMounted = false }) => {
  const isActive = tabKey === activeKey
  if (!isActive && !keepMounted) return null

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${tabKey}`}
      aria-labelledby={`tab-${tabKey}`}
      hidden={!isActive}
      className={cx(s.panel, className)}
    >
      {children}
    </div>
  )
}

TabPanel.propTypes = {
  tabKey: PropTypes.string.isRequired,
  activeKey: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  /** Keep the DOM alive when inactive — preserves scroll//filter state. */
  keepMounted: PropTypes.bool
}

export default Tabs
